import type { ConsumptionReport, ReportAccount, ReportMaster } from '../api/monitor';
import { formatLine } from './lines';

/** Rótulo da operadora a partir do código, para quando só o código chega. */
const OPERATOR_LABELS: Record<string, string> = {
  claro: 'Claro',
  vivo: 'Vivo',
};

export function operatorLabel(code: string): string {
  return OPERATOR_LABELS[code] ?? code;
}

/** `TEMPO TELECOM · Claro e Vivo · 48 linhas` — a opção do campo de master. */
export function masterLabel(master: ReportMaster): string {
  const operators = master.operators.map(operatorLabel);
  const where =
    operators.length > 1
      ? `${operators.slice(0, -1).join(', ')} e ${operators[operators.length - 1]}`
      : (operators[0] ?? 'sem operadora');

  return `${master.name} · ${where} · ${master.lines} linha${master.lines === 1 ? '' : 's'}`;
}

/** `Claro · CPF 06374300181 · 24 linhas` — a opção do campo de conta. */
export function accountLabel(account: ReportAccount): string {
  const lines = `${account.lines} linha${account.lines === 1 ? '' : 's'}`;

  return `${account.operator.name} · ${account.username} · ${lines}`;
}

/**
 * Como o relatório foi feito, para a lista.
 *
 * Aparece só quando **não** é o caminho padrão: marcar todo relatório com
 * "consulta por linha" seria repetir em cada item o que já é a regra. O que
 * precisa saltar aos olhos é o relatório que veio do portal, porque ele cobre
 * ciclos fechados em vez do consumo de agora.
 */
export function sourceLabel(report: ConsumptionReport): string | null {
  return report.source === 'vivo_portal_last3' ? 'portal Vivo · últimos ciclos' : null;
}

/** `10/06 – 09/07/2026` a partir das duas datas gravadas no relatório. */
export function periodLabel(report: ConsumptionReport): string {
  const dayMonth = (iso: string) => {
    const [, month, day] = iso.split('-');
    return `${day}/${month}`;
  };

  return `${dayMonth(report.periodStart)} – ${dayMonth(report.periodEnd)}/${report.periodEnd.slice(0, 4)}`;
}

/**
 * Segunda linha do item da lista: o filtro que gerou o relatório, mais o que a
 * coleta conseguiu apurar.
 *
 * As linhas sem consumo aparecem aqui porque um relatório "pronto" com metade
 * das linhas em branco não é a mesma coisa que um completo, e a diferença só
 * seria visível abrindo a planilha.
 */
export function reportSummary(report: ConsumptionReport): string {
  const parts = [periodLabel(report)];

  const origin = sourceLabel(report);
  if (origin) parts.push(origin);

  parts.push(report.account ? `conta ${report.account}` : 'todas as contas');

  if (report.line) {
    parts.push(`linha ${formatLine(report.line)}`);
  }

  if (report.status === 'ready') {
    parts.push(`${report.totalLines} linha${report.totalLines === 1 ? '' : 's'}`);

    // O par contratado/consumido é a resposta que o relatório dá; mostrá-lo aqui
    // evita baixar a planilha só para saber se o master estourou a franquia.
    const usage = usageLabel(report);
    if (usage) parts.push(usage);

    if (report.failedLines > 0) {
      parts.push(`${report.failedLines} sem consumo`);
    }
  }

  parts.push(whenLabel(report.finishedAt ?? report.createdAt));

  return parts.join(' · ');
}

/** `1.240,5 GB` — número grande no formato do país, sem casas demais. */
function gb(value: number): string {
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} GB`;
}

/**
 * `3.100 / 4.200 GB (73,8%)` — consumido sobre contratado.
 *
 * Nulo quando o relatório não tem os dois: relatório antigo, gerado antes de a
 * franquia contratada existir, não tem total nenhum gravado, e inventar zero
 * ali diria que o master não contratou nada.
 */
export function usageLabel(report: ConsumptionReport): string | null {
  const { totalConsumedGb: consumed, totalHiredGb: hired } = report;

  if (consumed === null || hired === null) return null;

  const share =
    hired > 0 ? ` (${((consumed / hired) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%)` : '';

  return `${gb(consumed)} de ${gb(hired)} contratados${share}`;
}

/** `Hoje 08:12`, `Ontem 16:40` ou `12/06 09:15`, como no design. */
export function whenLabel(iso: string): string {
  const date = new Date(iso);
  const time = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);

  const days = Math.floor((startOfToday.getTime() - new Date(iso).setHours(0, 0, 0, 0)) / 86_400_000);

  if (days <= 0) return `Hoje ${time}`;
  if (days === 1) return `Ontem ${time}`;

  return `${date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })} ${time}`;
}

export const STATUS_LABELS: Record<ConsumptionReport['status'], string> = {
  pending: 'Na fila',
  running: 'Processando',
  ready: 'Pronto',
  error: 'Erro',
};

/** Classes da pílula de status, nas cores que o design usa em cada estado. */
export const STATUS_PILL: Record<ConsumptionReport['status'], string> = {
  pending: 'bg-surface-200 text-ink-600',
  running: 'bg-[#E8EEFB] text-navy-800',
  ready: 'bg-ok-100 text-ok-600',
  error: 'bg-danger-100 text-danger-600',
};

/** Se a tela deve continuar perguntando ao servidor se o relatório terminou. */
export function isPending(report: ConsumptionReport): boolean {
  return report.status === 'pending' || report.status === 'running';
}
